import React, { useContext, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { newPaginationInput, PaginationInput } from "src/api/types";
import { useModalAction } from "src/components/modal-views/context";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import PermissionGate from "src/components/shared/permission-gate";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";
import GlobalContext from "src/context/global-context";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { CopyIcon } from "src/components/icons/copy-icon";
import moment from "moment";
import { Table } from "antd";
import { DateRangePicker } from "rsuite";
import { PlusIcon } from "src/components/icons/plus-icon";
import { DateRange } from "rsuite/esm/DateRangePicker";
import TerminalFormModal from "src/components/walletTerminal/TerminalFormModal";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import AddCardMerchant from "src/components/users/CardMerchants/AddMerchantModal";
import routes from "src/config/routes";
import { useNavigate } from "react-router-dom";

const VirtualCardMerchants = () => {
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { openModal, closeModal } = useModalAction();
  const navigate = useNavigate();

  const { canView, canList, canAdd } = usePermissions(
    permissionConstants.CARD_MERCHANTS
  );
  const canViewMerchants = canView || canList;

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    limit: LIST_PAGE_SIZE,
    page: 1,
  });

  const [addMerchantModalOpen, setAddMerchantModalOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [dateRangeValue, setDateRangeValue] = useState<DateRange | null>(null);

  const now = new Date();
  const prevMonth = new Date(now.setMonth(now.getMonth() - 1));

  const [startDate, endDate] = dateRangeValue || [];
  const { afterToday } = DateRangePicker;

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({
      ...searchParams,
      page: page,
    });
  };

  const { mutate: getMerchantsList, isLoading } = useMutation(
    client.cardMerchants.allCardMerchantsList,
    {
      onSuccess: (data: any) => {
        const dataObj = data?.data?.data;
        setTotal(data?.data?.pagination?.total);
        setDataSource(
          dataObj.map((item: any) => ({
            key: item.id,
            id: item.id,
            name: item.name,
            status: item.status,
            environment: item.environment,
            updated_at: item.updated_at,
            created_at: item.created_at,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: addMerchant, isLoading: addMerchantLoading } = useMutation(
    client.cardMerchants.addCardMerchant,
    {
      onSuccess: () => {
        setAlertText("Merchant Added Successfully");
        setAddMerchantModalOpen(false);
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") setAlertText(NetworkDisconnectMsg);
        else if (error.response)
          setAlertText(getErrorMessage(error.response.data));
        else setAlertText(APISomethingWrongMsg);

        setIsAlertOpened(true);
      },
    },
  );

  const { mutate: deleteTerminal, isLoading: deleteTerminalLoading } =
    useMutation(client.walletTerminal.remove, {
      onSuccess: (data: any) => {
        setAlertText("Terminal Deleted");
        onCallback(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    });

  const handleAddMerchantSubmit = (formData: any) => {
    if (!canAdd) {
      setAlertText("You do not have permission to add merchants");
      setIsAlertOpened(true);
      return;
    }
    addMerchant(formData);
  };

  const onCallback = (bSuccess: boolean) => {
    bSuccess && getMerchantsList(searchParams);
  };

  const onDelete = (key: number) => {
    openModal("GENERAL_DELETE", {
      id: key,
      callback: () => {
        deleteTerminal({ id: key });
        setTimeout(() => {
          closeModal();
        }, 100);
      },
      isLoading: () => deleteTerminalLoading,
    });
  };

  useEffect(() => {
    if (!isMounted || !canViewMerchants) {
      return;
    }
    getMerchantsList({
      ...searchParams,
      start_date: startDate
        ? moment(startDate).format("yyyy-MM-DD")
        : undefined,
      end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, canViewMerchants, searchParams.page, startDate, endDate]);

  const columns = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Merchant ID
        </p>
      ),
      dataIndex: "id",
      key: "id",
      render: (value: any) => (
        <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Environment
        </p>
      ),
      dataIndex: "environment",
      key: "environment",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {value}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created At
        </p>
      ),
      dataIndex: "created_at",
      key: "created_at",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Updated At
        </p>
      ),
      dataIndex: "updated_at",
      key: "updated_at",
      render: (value: any) => (
        <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
          {moment(value).format("YYYY-MM-DD HH:mm:ss")}
        </p>
      ),
    },
    {
      title: "Action",
      render: (_: any, record: any) => {
        if (!canView) return "—";
        return (
          <button
            className="text-blue-600"
            onClick={() => {
              navigate(routes.cardMerchantsView.replace(":id", record.id));
            }}
          >
            View
          </button>
        );
      },
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Action
    //     </p>
    //   ),
    //   dataIndex: "action",
    //   className: "w-[100px]",
    //   render: (_: any, record: any) => (
    //     <div className="flex gap-3 items-center">
    //       {/* <EditOutlined
    //         className="text-primary cursor-pointer text-[18px]"
    //         onClick={() => {
    //           setEditTerminalData(record);
    //           setAddMerchantModalOpen(true);
    //         }}
    //       /> */}
    //       <DeleteOutlined
    //         className="text-red-500 cursor-pointer text-[18px]"
    //         onClick={() => onDelete(Number(record.id))}
    //       />
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className="w-full h-full flex-col p-4 sm:p-6">
      {/* Centered content container with padding and rounded card */}
      <div className="w-full mx-auto px-2 lg:px-4">
        <div className="bg-white rounded-md shadow-sm p-4 sm:p-6">
          {/* Header: title + controls row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-[22px] sm:text-[28px] font-manrope text-primary font-medium leading-tight">
                Card Merchants
              </h1>
            </div>

            {/* Controls: datepicker + add button aligned */}
            <div className="flex items-center gap-3">
              {/* <div className="w-full sm:w-auto">
                <DateRangePicker
                  format="dd/MM/yyyy"
                  size="lg"
                  placement="bottomEnd"
                  style={{ width: isMobile ? "100%" : 320 }}
                  value={dateRangeValue}
                  onChange={setDateRangeValue}
                  defaultCalendarValue={[prevMonth, now]}
                  shouldDisableDate={afterToday()}
                  ranges={[]}
                />
              </div> */}

              <PermissionGate
                module={[permissionConstants.CARD_MERCHANTS]}
                action="can_add"
              >
                <div>
                  <button
                    className="flex items-center gap-2 px-3 py-2 bg-white border rounded-md hover:bg-[#F3F4F6]"
                    onClick={() => {
                      setAddMerchantModalOpen(true);
                    }}
                  >
                    <span className="font-manrope text-[14px] font-medium text-primary">
                      Add New Merchant
                    </span>
                    <PlusIcon className="w-[18px] h-[18px] text-primary" />
                  </button>
                </div>
              </PermissionGate>
            </div>
          </div>

          {/* Table wrapper with horizontal scroll if needed */}
          {!canViewMerchants ? (
            <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 mt-2 sm:mt-4 text-center shadow-sm">
              <p className="font-manrope text-[16px] font-semibold text-secondary">
                You do not have permission to view card merchants.
              </p>
            </div>
          ) : (
            <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
              <Table
                loading={isLoading}
                columns={columns}
                dataSource={dataSource}
                pagination={{
                  total,
                  current: searchParams.page,
                  onChange: onPageChange,
                  position: ["bottomCenter"],
                  defaultPageSize: LIST_PAGE_SIZE,
                  showSizeChanger: false,
                }}
                rowClassName="editable-row"
                // scroll={{ x: "max-content" }}
              />
            </div>
          )}
        </div>
      </div>

      <AddCardMerchant
        open={addMerchantModalOpen}
        onClose={() => setAddMerchantModalOpen(false)}
        onSubmit={handleAddMerchantSubmit}
        loading={addMerchantLoading}
        // initialData={editTerminalData}
      />
    </div>
  );
};

export default VirtualCardMerchants;
